import type Lenis from 'lenis';
import { isTouch } from '../utils';

// ─── Config ──────────────────────────────────────────
const CONFIG = {
    thumbMinHeight: 40,        // px
    thumbWidth: 6,             // px (idle)
    thumbWidthHover: 8,        // px (hover/drag)
    autoHideDelay: 1200,       // ms
    scrollDuration: 0.6,       // s (clic sur track)
    zIndex: 9999,
};

// ─── State ───────────────────────────────────────────
let track: HTMLElement | null = null;
let thumb: HTMLElement | null = null;
let isDragging = false;
let startY = 0;
let startScroll = 0;
let hideTimeout: ReturnType<typeof setTimeout>;
let lenisRef: Lenis | null = null;
let resizeHandler: (() => void) | null = null;
let mouseMoveHandler: ((e: MouseEvent) => void) | null = null;
let mouseUpHandler: (() => void) | null = null;

// ─── Init ────────────────────────────────────────────
export const init = (lenis: Lenis): void => {
    // Pas de scrollbar custom sur touch
    if (isTouch()) return;

    lenisRef = lenis;

    // Cacher la scrollbar native
    document.documentElement.classList.add('has-custom-scrollbar');

    // Créer le DOM
    track = document.createElement('div');
    track.className = 'scrollbar-track';

    thumb = document.createElement('div');
    thumb.className = 'scrollbar-thumb';

    track.appendChild(thumb);
    document.body.appendChild(track);

    // Bindings
    bindScrollEvents();
    bindTrackClick();
    bindDrag();
    updateThumbSize();

    resizeHandler = updateThumbSize;
    window.addEventListener('resize', resizeHandler);
};

// ─── Destroy ─────────────────────────────────────────
export const destroy = (): void => {
    if (track) {
        track.remove();
        track = null;
        thumb = null;
    }

    document.documentElement.classList.remove('has-custom-scrollbar');

    if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
        resizeHandler = null;
    }

    if (mouseMoveHandler) {
        window.removeEventListener('mousemove', mouseMoveHandler);
        mouseMoveHandler = null;
    }

    if (mouseUpHandler) {
        window.removeEventListener('mouseup', mouseUpHandler);
        mouseUpHandler = null;
    }

    clearTimeout(hideTimeout);
    lenisRef = null;
};

// ─── Internals ───────────────────────────────────────

/** Calcule la hauteur du thumb selon le ratio viewport/contenu */
const updateThumbSize = (): void => {
    if (!thumb) return;
    const ratio = window.innerHeight / document.documentElement.scrollHeight;
    const height = Math.max(ratio * window.innerHeight, CONFIG.thumbMinHeight);
    thumb.style.height = `${height}px`;
};

/** Synchronise la position du thumb + gère l'auto-hide */
const bindScrollEvents = (): void => {
    if (!lenisRef || !track || !thumb) return;

    const thumbEl = thumb;
    const trackEl = track;

    lenisRef.on('scroll', ({ progress }: { progress: number }) => {
        const thumbHeight = thumbEl.getBoundingClientRect().height;
        const maxTravel = window.innerHeight - thumbHeight;
        thumbEl.style.transform = `translate3d(0, ${progress * maxTravel}px, 0)`;

        // Auto-hide
        trackEl.classList.add('is-visible');
        clearTimeout(hideTimeout);
        hideTimeout = setTimeout(() => {
            if (!isDragging) {
                trackEl.classList.remove('is-visible');
            }
        }, CONFIG.autoHideDelay);
    });
};

/** Clic sur le track → scroll à la position correspondante */
const bindTrackClick = (): void => {
    if (!track || !lenisRef) return;

    const lenis = lenisRef;

    track.addEventListener('click', (e: MouseEvent) => {
        // Ignorer si on clique sur le thumb
        if ((e.target as HTMLElement).classList.contains('scrollbar-thumb')) return;

        const clickRatio = e.clientY / window.innerHeight;
        const scrollTarget = clickRatio * lenis.limit;
        lenis.scrollTo(scrollTarget, { duration: CONFIG.scrollDuration });
    });
};

/** Drag du thumb pour scroller */
const bindDrag = (): void => {
    if (!thumb || !track || !lenisRef) return;

    const thumbEl = thumb;
    const trackEl = track;
    const lenis = lenisRef;

    thumbEl.addEventListener('mousedown', (e: MouseEvent) => {
        isDragging = true;
        startY = e.clientY;
        startScroll = lenis.scroll;
        document.body.style.userSelect = 'none';
        trackEl.classList.add('is-dragging');
        e.preventDefault();
    });

    mouseMoveHandler = (e: MouseEvent) => {
        if (!isDragging) return;
        const deltaY = e.clientY - startY;
        const thumbHeight = thumbEl.getBoundingClientRect().height;
        const maxTravel = window.innerHeight - thumbHeight;
        const scrollDelta = (deltaY / maxTravel) * lenis.limit;
        lenis.scrollTo(startScroll + scrollDelta, { immediate: true });
    };

    mouseUpHandler = () => {
        if (!isDragging) return;
        isDragging = false;
        document.body.style.userSelect = '';
        trackEl.classList.remove('is-dragging');
    };

    window.addEventListener('mousemove', mouseMoveHandler);
    window.addEventListener('mouseup', mouseUpHandler);
};
